import type { EquipmentConfig, ExtraTagConfig, StreamConfig } from '../../engine/plant/types';

/**
 * Municipal water treatment plant — 12 MLD (500 m³/h).
 * Sizing, design conditions and sources: docs/PROCESS_MODELS.md §2.10 and §5.1.
 *
 * Hydraulic grade line (water-surface elevations, m above the river):
 *   rapid mix 10.00 → flocculation 9.80 → clarifier 9.47 (launder crest 9.45)
 *   → filter channel 8.90 → filter boxes 8.50 → contact tank → clearwell 3.25
 */

export const SPECIES = ['ntu', 'floc', 'alum', 'cl2', 'alk'] as const;

export const extraTags: ExtraTagConfig[] = [
  { name: 'S-101.Q', units: 'm3/h', rangeLo: 0, rangeHi: 800, decimals: 1, kind: 'state', description: 'Raw water flow to rapid mix' },
  { name: 'S-302.Q', units: 'm3/h', rangeLo: 0, rangeHi: 800, decimals: 1, kind: 'state', description: 'Settled water flow to the filter channel' },
  { name: 'S-DIST.Q', units: 'm3/h', rangeLo: 0, rangeHi: 1200, decimals: 1, kind: 'state', description: 'Distribution flow' },
  { name: 'SPD-101', units: '%', rangeLo: 0, rangeHi: 100, decimals: 1, kind: 'output', description: 'Intake pump speed demand' },
  { name: 'SPD-701', units: '%', rangeLo: 0, rangeHi: 100, decimals: 1, kind: 'output', description: 'High-lift pump speed demand' },
];

const filter = (n: number, capturedKg: number, runH: number): EquipmentConfig => ({
  id: `F-40${n}`,
  name: `Rapid gravity filter ${n}`,
  type: 'filter',
  area: 25, // m², 5 m/h loading at 125 m³/h  [AWWA]
  commandTag: `FV-40${n}.POS`,
  maxFlow: 320, // m³/h at a fully open valve and design driving head
  staticDrop: 2.5, // underdrain to clearwell inlet
  designHead: 3.7, // 1.5 m over the media + 2.5 m static − 0.3 m clean-bed loss
  headlossClean: 0.3,
  headlossTerminal: 2.5,
  capacityKg: 22, // ⇒ a ~35 h run to the headloss alarm at design conditions
  ntuFloor: 0.03,
  ntuRipening: 0.25,
  tauRipening: 900,
  ntuBreakthrough: 0.9,
  waterVolume: 37.5,
  initialLevel: 1.5,
  elevation: 7.0,
  initialCapturedKg: capturedKg,
  initialRunTimeH: runH,
});

const inletValve = (n: number): EquipmentConfig => ({
  id: `IV-40${n}`,
  name: `Filter ${n} inlet valve`,
  type: 'valve',
  cv: 3000,
  characteristic: 'linear',
  tau: 6,
  strokeTime: 30,
  failMode: 'failClosed',
  commandTag: `IV-40${n}.CMD`,
  initialPosition: 1,
  positionTransmitter: true,
});

const rateValve = (n: number): EquipmentConfig => ({
  id: `FV-40${n}`,
  name: `Filter ${n} rate-of-flow valve`,
  type: 'valve',
  cv: 400,
  characteristic: 'equalPercentage',
  tau: 3,
  deadband: 0.5,
  failMode: 'failClosed',
  commandTag: `FV-40${n}.CMD`,
  initialPosition: 0.5,
  positionTransmitter: true,
});

export const equipment: EquipmentConfig[] = [
  // ── Intake ────────────────────────────────────────────────────────────────
  {
    id: 'SRC-001',
    name: 'River intake',
    type: 'source',
    pressure: 101_325,
    temperature: 285.15, // 12 °C
    composition: { ntu: 8, floc: 0, alum: 0, cl2: 0, alk: 60 },
    profiles: [
      // Turbidity wanders on a slow diurnal cycle with AR(1) noise; storms are a fault.
      { field: 'ntu', kind: 'diurnal', mean: 8, amplitude: 1.2, periodS: 86400, phaseS: 21600, noiseSigma: 0.04, noisePhi: 0.995, lo: 0.5, hi: 400 },
      { field: 'temperature', kind: 'diurnal', mean: 285.15, amplitude: 0.8, periodS: 86400, phaseS: 43200, noiseSigma: 0.0005, noisePhi: 0.999, lo: 275, hi: 305 },
    ],
  },
  {
    id: 'P-101A',
    name: 'Intake pump A',
    type: 'pump',
    h0: 32,
    kCurve: 741,
    qBep: 0.0972, // 350 m³/h
    etaBep: 0.78,
    vfd: true,
    commandTag: 'SPD-101',
    startTime: 8,
    coastTime: 15,
    npshr: 3,
  },
  {
    id: 'P-101B',
    name: 'Intake pump B',
    type: 'pump',
    h0: 32,
    kCurve: 741,
    qBep: 0.0972,
    etaBep: 0.78,
    vfd: true,
    commandTag: 'SPD-101',
    startTime: 8,
    coastTime: 15,
    npshr: 3,
  },
  { id: 'HD-101', name: 'Raw water header', type: 'header', pressure: 2.0e5, capacitance: 2e-7, temperature: 285.15 },

  // ── Coagulation and flocculation ─────────────────────────────────────────
  {
    id: 'PD-201',
    name: 'Alum dosing pump',
    type: 'doser',
    commandTag: 'PD-201.CMD',
    maxFlow: 300, // L/h
    tau: 8,
    species: 'alum',
    stockConcentration: 100_000, // mg/L of alum in a 10 % w/v solution
  },
  {
    id: 'MX-201',
    name: 'Rapid mix',
    type: 'coagulation',
    volume: 4.2, // 30 s at design flow
    depth: 2.0,
    elevation: 8.0,
    stockConcentration: 100_000,
    doseFlowTag: 'PD-201.LPH',
    optimumBase: 10, // D* = 10 + 2.5·√NTU  [AWWA ch. 6, MWH ch. 9]
    optimumSlope: 2.5,
    etaMax: 0.92,
    widthUnder: 0.55,
    widthOver: 0.4, // over-dosing hurts faster — charge reversal
    alkPerDose: 0.5, // mg/L alkalinity per mg/L alum  [MWH]
    initialComposition: { ntu: 8, floc: 0.9, alum: 15.5, alk: 52 },
  },
  {
    id: 'FL-202',
    name: 'Flocculation basin',
    type: 'floc',
    stages: 3,
    volumePerStage: 55.6, // 20 min total at design flow
    depth: 3.5,
    elevation: 6.3,
    g: 40, // velocity gradient, 1/s
    gtReference: 48_000, // G·t at design  [AWWA target 30–60 k]
    initialFloc: 0.9,
  },

  // ── Clarification ────────────────────────────────────────────────────────
  {
    id: 'CL-301',
    name: 'Sedimentation clarifier',
    type: 'clarifier',
    area: 333, // SOR 1.5 m/h at design  [AWWA]
    depth: 3.0,
    elevation: 6.5,
    eta0: 0.97,
    ks: 8.0,
    ntuFloor: 0.3,
    initialNtu: 1.4,
    blowdownTag: 'BV-301.CMD',
    blowdownFlow: 15, // m³/h fully open
    blanketArea: 30, // sludge hopper footprint
    initialBlanket: 0.9,
    sludgeSolidsFrac: 0.003,
  },

  // ── Filtration ───────────────────────────────────────────────────────────
  {
    id: 'FCH-401',
    name: 'Filter inlet channel',
    type: 'tank',
    shape: 'vertical',
    area: 100,
    height: 2.4,
    initialLevel: 1.5,
    open: true,
    elevation: 7.4,
    temperature: 285.15,
    composition: { ntu: 1.2, floc: 0, alum: 15.9, cl2: 0, alk: 52 },
  },
  inletValve(1), inletValve(2), inletValve(3), inletValve(4),
  rateValve(1), rateValve(2), rateValve(3), rateValve(4),
  // Staggered run times so the filters do not all need backwashing at once.
  // Staggered through the run: no two filters ever need washing at the same time, and
  // none reaches the headloss alarm within a single shift at design conditions.
  filter(1, 2.0, 4),
  filter(2, 5.5, 11),
  filter(3, 9.0, 18),
  filter(4, 12.5, 25),

  // ── Disinfection and storage ─────────────────────────────────────────────
  {
    id: 'PD-601',
    name: 'Chlorine dosing pump',
    type: 'doser',
    commandTag: 'PD-601.CMD',
    maxFlow: 20, // L/h
    tau: 5,
    species: 'cl2',
    stockConcentration: 120_000, // mg/L available chlorine
  },
  {
    id: 'CT-601',
    name: 'Chlorine contact tank',
    type: 'contact',
    volume: 300, // 36 min at design flow, 28 min at the diurnal peak
    tanks: 8, // ⇒ t10/T ≈ 0.7  [EPA]
    t10Ratio: 0.7,
    decayK: 0.15 / 3600, // 0.15 /h bulk decay  [MWH]
    immediateDemand: 0.55, // mg/L consumed before any residual appears  [MWH ch. 13]
    doseFlowTag: 'PD-601.LPH',
    stockConcentration: 120_000,
    ctRequired: 17.5, // 0.5-log Giardia, 10 °C, pH 7  [EPA]
    initialResidual: 1.0,
  },
  {
    id: 'PD-602',
    name: 'Caustic dosing pump',
    type: 'doser',
    commandTag: 'PD-602.CMD',
    maxFlow: 80, // L/h — enough to hold pH through a storm-strength alum dose
    tau: 6,
    species: 'alk',
    stockConcentration: 312_500, // mg/L as CaCO₃ equivalent (25 % NaOH)
  },
  {
    id: 'CW-701',
    name: 'Clearwell',
    type: 'tank',
    shape: 'vertical',
    area: 600,
    // 3 000 m³ — a quarter of a day's production, which is what it takes to absorb the
    // diurnal demand swing without the treatment train having to chase it.
    height: 5.0,
    initialLevel: 3.0, // 60 %
    open: true,
    elevation: 0,
    temperature: 285.15,
    composition: { ntu: 0.05, floc: 0, alum: 15.9, cl2: 1.0, alk: 63.3 },
  },
  {
    id: 'PH-601',
    name: 'Final water pH',
    type: 'ph',
    alkTag: 'CW-701.C_alk',
    // Total inorganic carbon chosen so raw water (60 mg/L alkalinity) sits at pH 7.4.
    totalCarbonate: 1.3055e-3,
  },

  // ── Distribution ─────────────────────────────────────────────────────────
  {
    id: 'P-701A',
    name: 'High-lift pump A',
    type: 'pump',
    h0: 78,
    kCurve: 1482,
    qBep: 0.0972,
    etaBep: 0.8,
    vfd: true,
    commandTag: 'SPD-701',
    startTime: 8,
    coastTime: 15,
    npshr: 3,
  },
  {
    id: 'P-701B',
    name: 'High-lift pump B',
    type: 'pump',
    h0: 78,
    kCurve: 1482,
    qBep: 0.0972,
    etaBep: 0.8,
    vfd: true,
    commandTag: 'SPD-701',
    startTime: 8,
    coastTime: 15,
    npshr: 3,
  },
  { id: 'HD-701', name: 'Distribution header', type: 'header', pressure: 5.5e5, capacitance: 1e-7, temperature: 285.15 },
  {
    id: 'DIST',
    name: 'Distribution demand',
    type: 'demand',
    // Two-peak municipal curve [AWWA ch. 3]. The shift starts at 06:00, so t = 0 is
    // 06:00: morning peak ≈ 09:00 (1.29×), midday shoulder, evening peak ≈ 21:00,
    // minimum ≈ 00:00 (0.76×). Amplitudes are held below the plant's hydraulic limit —
    // the clearwell absorbs the difference, which is what a clearwell is for.
    average: 500,
    amp1: 0.24,
    amp2: 0.12,
    peak1S: 0,
    peak2S: 0,
    noiseSigma: 0.015,
    noisePhi: 0.98,
    // Network resistance at 500 m³/h with 1.51 bar across it (4.5 barg header,
    // 3.0 barg at the far end of the district).
    kDesign: 7.83e6,
  },
  // ── Calculated tags ──────────────────────────────────────────────────────
  // Flow- and turbidity-paced alum demand, as a percentage of the dosing pump.
  //   OP_ff = (D0 + kD·√NTU_raw) · Q / (maxFlow · stock / 1e6)
  // With stock 100 g/L and a 300 L/h pump that reduces to (10 + 2.5·√NTU)·Q/300.
  {
    id: 'CALC-201',
    name: 'Alum dose demand',
    type: 'calc',
    out: { name: 'CALC-201.OUT', units: '%', rangeLo: 0, rangeHi: 100, decimals: 2, description: 'Flow- and turbidity-paced alum demand' },
    ops: [
      { op: 'tag', tag: 'AT-101.PV' },
      { op: 'sqrt' },
      { op: 'mul', value: 2.5 },
      { op: 'add', value: 10 },
      { op: 'mulTag', tag: 'FT-101.PV' },
      { op: 'div', value: 300 },
      { op: 'clamp', lo: 0, hi: 90 },
      { op: 'lag', tau: 60 },
    ],
  },
  // Flow-paced chlorine demand at the design 1.6 mg/L, as a percentage of the pump.
  {
    id: 'CALC-601',
    name: 'Chlorine dose demand',
    type: 'calc',
    out: { name: 'CALC-601.OUT', units: '%', rangeLo: 0, rangeHi: 100, decimals: 2, description: 'Flow-paced chlorine demand' },
    ops: [
      { op: 'tag', tag: 'FT-101.PV' },
      { op: 'mul', value: 1.6 / 24 },
      { op: 'clamp', lo: 0, hi: 95 },
      { op: 'lag', tau: 30 },
    ],
  },
  // Total filtered water reaching the contact tank — the plant's real throughput.
  {
    id: 'CALC-405',
    name: 'Total filtered flow',
    type: 'calc',
    out: { name: 'CALC-405.OUT', units: 'm3/h', rangeLo: 0, rangeHi: 900, decimals: 1, description: 'Total filtered water flow' },
    ops: [
      { op: 'tag', tag: 'F-401.Q' },
      { op: 'addTag', tag: 'F-402.Q' },
      { op: 'addTag', tag: 'F-403.Q' },
      { op: 'addTag', tag: 'F-404.Q' },
    ],
  },
  // Number of filters available for service — used to explain the flow split on the HMI.
  {
    id: 'CALC-406',
    name: 'Filters in service',
    type: 'calc',
    out: { name: 'CALC-406.OUT', units: '', rangeLo: 0, rangeHi: 4, decimals: 0, description: 'Filters in service' },
    ops: [
      { op: 'tag', tag: 'F-401.AVAIL' },
      { op: 'addTag', tag: 'F-402.AVAIL' },
      { op: 'addTag', tag: 'F-403.AVAIL' },
      { op: 'addTag', tag: 'F-404.AVAIL' },
    ],
  },

  { id: 'SNK-DIST', name: 'Distribution', type: 'sink', pressure: 4.0e5 },
  { id: 'SNK-SLUDGE', name: 'Sludge lagoon', type: 'sink', pressure: 101_325 },
  { id: 'SNK-WASTE', name: 'Washwater recovery', type: 'sink', pressure: 101_325 },
];

/**
 * Streams, in topological order. `transfer` branches read their source node's
 * accumulated inflow, so everything feeding that node must appear earlier — the config
 * validator enforces this.
 */
export const streams: StreamConfig[] = [
  { id: 'S-RAW-A', from: 'SRC-001', to: 'HD-101', kind: 'pressure', pump: 'P-101A', k: 3e5, checkValve: true, qMax: 0.3 },
  { id: 'S-RAW-B', from: 'SRC-001', to: 'HD-101', kind: 'pressure', pump: 'P-101B', k: 3e5, checkValve: true, qMax: 0.3 },
  { id: 'S-101', from: 'HD-101', to: 'MX-201', kind: 'pressure', k: 2.03e6, qMax: 0.5, flowOutTag: 'S-101.Q' },

  { id: 'S-202', from: 'MX-201', to: 'FL-202', kind: 'transfer' },
  { id: 'S-301', from: 'FL-202', to: 'CL-301', kind: 'transfer' },
  { id: 'S-SLUDGE', from: 'CL-301', to: 'SNK-SLUDGE', kind: 'demand', flowTag: 'CL-301.BLOWDOWN' },
  { id: 'S-302', from: 'CL-301', to: 'FCH-401', kind: 'weir', weirLength: 30, weirCrest: 9.45, flowOutTag: 'S-302.Q' },

  { id: 'S-401', from: 'FCH-401', to: 'F-401', kind: 'pressure', valve: 'IV-401', k: 3.26e6, qMax: 0.15 },
  { id: 'S-402', from: 'FCH-401', to: 'F-402', kind: 'pressure', valve: 'IV-402', k: 3.26e6, qMax: 0.15 },
  { id: 'S-403', from: 'FCH-401', to: 'F-403', kind: 'pressure', valve: 'IV-403', k: 3.26e6, qMax: 0.15 },
  { id: 'S-404', from: 'FCH-401', to: 'F-404', kind: 'pressure', valve: 'IV-404', k: 3.26e6, qMax: 0.15 },

  { id: 'S-411', from: 'F-401', to: 'CT-601', kind: 'demand', flowTag: 'F-401.Q' },
  { id: 'S-412', from: 'F-402', to: 'CT-601', kind: 'demand', flowTag: 'F-402.Q' },
  { id: 'S-413', from: 'F-403', to: 'CT-601', kind: 'demand', flowTag: 'F-403.Q' },
  { id: 'S-414', from: 'F-404', to: 'CT-601', kind: 'demand', flowTag: 'F-404.Q' },
  { id: 'S-421', from: 'F-401', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-401.QW' },
  { id: 'S-422', from: 'F-402', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-402.QW' },
  { id: 'S-423', from: 'F-403', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-403.QW' },
  { id: 'S-424', from: 'F-404', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-404.QW' },

  { id: 'S-601', from: 'CT-601', to: 'CW-701', kind: 'transfer' },
  { id: 'S-NAOH', from: 'PD-602', to: 'CW-701', kind: 'demand', flowTag: 'PD-602.Q' },

  // Backwash water is drawn from the clearwell and discarded — a real inventory cost.
  { id: 'S-BW1', from: 'CW-701', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-401.QBW' },
  { id: 'S-BW2', from: 'CW-701', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-402.QBW' },
  { id: 'S-BW3', from: 'CW-701', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-403.QBW' },
  { id: 'S-BW4', from: 'CW-701', to: 'SNK-WASTE', kind: 'demand', flowTag: 'F-404.QBW' },

  { id: 'S-702A', from: 'CW-701', to: 'HD-701', kind: 'pressure', pump: 'P-701A', k: 2e5, checkValve: true, qMax: 0.3 },
  { id: 'S-702B', from: 'CW-701', to: 'HD-701', kind: 'pressure', pump: 'P-701B', k: 2e5, checkValve: true, qMax: 0.3 },
  {
    id: 'S-DIST',
    from: 'HD-701',
    to: 'SNK-DIST',
    kind: 'pressure',
    kTag: 'DIST.K',
    k: 0,
    qMax: 0.6,
    flowOutTag: 'S-DIST.Q',
  },
];
